import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Mail } from 'lucide-react';
import { API, getHeaders, formatNum } from '../portalShared';
import { trackBookDemoClick } from '../../../utils/analytics';

function CampaignManagerCard({ onOpenMessages }) {
  const [cm, setCm] = useState(null);
  useEffect(() => {
    axios.get(`${API}/portal/campaign-manager`, { headers: getHeaders() })
      .then(r => setCm(r.data.campaign_manager))
      .catch(() => {});
  }, []);
  if (!cm) return null;
  const initials = (cm.name || 'J F').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="mb-5 bg-[#111827] border border-white/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="cm-card">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-12 h-12 rounded-full bg-[#FF4343]/15 border border-[#FF4343]/40 flex items-center justify-center text-[#FF4343] font-mono text-sm font-bold flex-shrink-0">
          {cm.avatar_url ? <img src={cm.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : initials}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343]">Your Campaign Manager</p>
          <p className="text-sm font-semibold text-white truncate" data-testid="cm-card-name">{cm.name}</p>
          <p className="text-[10px] text-[#9CA3AF] truncate font-mono">{cm.email}</p>
        </div>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <button onClick={onOpenMessages} data-testid="cm-message-btn"
          className="text-xs px-3 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5" /> Message
        </button>
        <a href={`mailto:${cm.email}`} data-testid="cm-email-btn"
          className="text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> Email
        </a>
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [hideOnboardingCTA, setHideOnboardingCTA] = useState(() => localStorage.getItem('brand_onboarding_cta_dismissed') === '1');
  useEffect(() => { axios.get(`${API}/portal/dashboard`, { headers: getHeaders() }).then(r => setData(r.data)).catch(() => {}); }, []);
  const openMessages = () => {
    if (onNavigate) onNavigate('messages');
    else window.location.search = '?tab=messages';
  };
  if (!data) return <p className="text-[#9CA3AF]">Loading...</p>;
  const isNewBrand = (data.active_campaigns || 0) === 0;
  return (
    <div data-testid="portal-dashboard">
      <CampaignManagerCard onOpenMessages={openMessages} />
      {/* New-brand onboarding meeting CTA */}
      {isNewBrand && !hideOnboardingCTA && (
        <div className="mb-6 bg-gradient-to-br from-[#FF4343]/15 via-[#FF4343]/5 to-[#111827] border border-[#FF4343]/40 p-5" data-testid="brand-onboarding-cta">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343] mb-1">Welcome to Freddy Leads</p>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-1">Get a 15-min walkthrough from the founders</h3>
              <p className="text-xs text-[#9CA3AF] leading-relaxed">Jon &amp; Noah will help you pick the right offering, set a realistic budget, source the best executives in your industry, and run your first campaign. No pitch — just a working session.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a href="https://meetings.hubspot.com/jon980" target="_blank" rel="noopener noreferrer"
                data-testid="brand-book-onboarding-call"
                onClick={() => trackBookDemoClick({ source: 'brand_portal_onboarding_cta' })}
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2.5 text-xs font-semibold hover:bg-[#E03030] inline-block">
                Book My Walkthrough →
              </a>
              <button onClick={() => { setHideOnboardingCTA(true); localStorage.setItem('brand_onboarding_cta_dismissed', '1'); }}
                data-testid="dismiss-onboarding-cta"
                className="text-[#6B7280] hover:text-white text-[10px] uppercase font-mono px-2">Dismiss</button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Active Campaigns', value: data.active_campaigns, color: 'text-white' },
          { label: 'Total Impressions', value: formatNum(data.total_impressions), color: 'text-[#FF4343]' },
          { label: 'Total Spend', value: `$${data.total_spend.toLocaleString()}`, color: 'text-[#FF4343]' },
          { label: 'Avg CPM', value: `$${data.avg_cpm}`, color: 'text-[#10B981]' },
        ].map(s => (
          <div key={s.label} className="bg-[#111827] border border-white/10 p-4 sm:p-5 min-w-0">
            <span className={`block font-mono text-xl sm:text-3xl font-bold tabular-nums leading-tight truncate ${s.color}`}>{s.value}</span>
            <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1 truncate">{s.label}</p>
          </div>
        ))}
      </div>
      {data.campaigns?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3">Your Campaigns</h3>
          <div className="space-y-2">
            {data.campaigns.map(c => (
              <div key={c.id} className="bg-[#111827] border border-white/10 p-4 flex items-center justify-between">
                <div><span className="text-sm text-white">{c.title}</span><span className="text-[10px] text-[#6B7280] ml-2">{c.industry}</span></div>
                <span className={`text-[10px] font-mono uppercase ${c.status === 'active' ? 'text-[#10B981]' : 'text-[#9CA3AF]'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
